import {flatten, sortBy} from 'lodash'
import {createSelector} from 'reselect'

import {IPrescriptionData} from 'core/api/patient'
import {ISelectSubject, IState, ISubject, TreatmentData} from './model'
import {ISubjectTreatmentReport} from './treatmentReport'

export const selectSubjects = createSelector<{subjects: IState}, ISubject[], ISelectSubject[]>(
  ({subjects}: {subjects: IState}) => subjects.items,
  (items) => items.map((item) => ({
    ...item,
    amblyopicEye: item.prescriptions[0] ? item.prescriptions[0].amblyopicEye : undefined,
  })),
)

export const selectSubject = ({subjects}: {subjects: IState}, subjectIdentifier: string): ISelectSubject | null => {
  const subject = subjects.items.find((s) => s.subjectIdentifier === subjectIdentifier)
  if (!subject) return null
  return {
    ...subject,
    amblyopicEye: subject.prescriptions[0] && subject.prescriptions[0].amblyopicEye,
  }
}

/**
 * Prescriptions for the specified subject, ordered by `activatedTime` descending.
 */
export const selectSubjectPrescriptions =
  createSelector<{subjects: IState}, string, ISubject | null, IPrescriptionData[] | null>(
    selectSubject,
    (subject) => {
      if (!subject) return null
      return sortBy(subject.prescriptions, (p) => Date.parse(p.activatedTime || '0')).reverse()
    },
  )

export const selectSubjectLastActivatedPrescription = (
  {subjects}: {subjects: IState},
  subjectIdentifier: string,
): IPrescriptionData | null => {
  const prescriptions = selectSubjectPrescriptions({subjects}, subjectIdentifier)
  if (prescriptions === null) return null
  if (prescriptions.length <= 0) return null
  return prescriptions[0]
}

export const selectSubjectTreatmentReport = (
  {subjects}: {subjects: IState},
  subjectIdentifier: string,
): ISubjectTreatmentReport | null => {
  const tr = subjects.treatmentReport.reports.find((r) => r.subjectIdentifier === subjectIdentifier)
  if (!tr) return null
  return tr
}

export const selectSubjectUsageByDate = (
  {subjects}: {subjects: IState},
  subjectIdentifier: string,
): {[date: string]: number} => {
  const tr = selectSubjectTreatmentReport({subjects}, subjectIdentifier)
  if (!tr) return {}
  const prescriptions = tr.treatmentReport.prescriptions
  const dailyUsages = flatten(prescriptions.map((p) => p.dailyUsages))
  return dailyUsages.reduce((usageByDate: {[date: string]: number}, dailyUsage) => {
    const date = dailyUsage.day
    return {
      ...usageByDate,
      [date]: dailyUsage.completedDosage + (usageByDate[date] || 0),
    }
  }, {})
}

const selectSubjectDosageByDate = (
  {subjects}: {subjects: IState},
  subjectIdentifier: string,
): {[date: string]: number} => {
  const tr = selectSubjectTreatmentReport({subjects}, subjectIdentifier)
  if (!tr) return {}
  const dailyDosages = tr.treatmentReport.dailyDosages
  return dailyDosages.reduce((dosageByDate, dailyDosage) => ({
    ...dosageByDate,
    [dailyDosage.date]: dailyDosage.dailyDosage,
  }), {})
}

/**
 * Treatment data for the subject, sorted by date descending.
 */
export const selectSubjectTreatmentData = (
  {subjects}: {subjects: IState},
  subjectIdentifier: string,
): TreatmentData | null => {
  const tr = selectSubjectTreatmentReport({subjects}, subjectIdentifier)
  if (!tr) return null
  const usageByDate = selectSubjectUsageByDate({subjects}, subjectIdentifier)
  const dosageByDate = selectSubjectDosageByDate({subjects}, subjectIdentifier)
  const sortedDates = Array.from(new Set(Object.keys(usageByDate).concat(Object.keys(dosageByDate)))).sort().reverse()

  const dailyTreatmentData = sortedDates.map((date) => ({
    date,
    completed: usageByDate[date] || 0,
    prescribed: dosageByDate[date] || 0,
  }))
  const totalRecommendedDosage = tr.treatmentReport.totalRecommendedDosage
  const totalCompletedDosage = (() => {
    if (usageByDate === null) return 0
    return Object.keys(usageByDate).reduce((total, date) => total + usageByDate[date], 0)
  })()

  return {
    dailyTreatmentData,
    totalRecommendedDosage,
    totalCompletedDosage,
  }
}

/**
 * Treatment data for the subject, sorted by date ascending.
 * TODO: This should take advantage of reselect calculation caching. I think maybe something like the
 * createSelectorCreator can help us:
 * https://github.com/reduxjs/reselect#createselectorcreatormemoize-memoizeoptions
 */
export const selectSubjectTreatmentDataDateAsc = (
  {subjects}: {subjects: IState},
  subjectIdentifier: string,
): TreatmentData | null => {
  const treatmentData = selectSubjectTreatmentData({subjects}, subjectIdentifier)
  if (treatmentData === null) return treatmentData
  return {
    ...treatmentData,
    dailyTreatmentData: treatmentData.dailyTreatmentData.reverse(),
  }
}



// WEBPACK FOOTER //
// ./~/tslint-loader??ref--0!./study_coordinator/src/store/subjects/selectors.ts